import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    computed,
    inject,
    signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipantImage,
    ExtendedDisplayType,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType, isPlayerMatched } from '@frontend/sports/participant-image/feature';
import { NOT_APPLICABLE, TrackingService, TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxFor } from '@rx-angular/template/for';
import { ceil } from 'lodash-es';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupRendererDirective } from './option-group-renderer.directive';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { BaseOptionGroupStatisticsService } from './player-statistics/base-option-group-statistics.service';
import { OptionGroupPlayerStatisticsService, StatisticsParams } from './player-statistics/option-group-player-statistics.service';
import { PlayerStatisticsComponent } from './player-statistics/player-statistics.component';
import { PlayerStatisticsService, SeasonStatisticsType } from './player-statistics/player-statistics.service';
import { PlayerStatsComponent } from './player-stats/player-stats.component';

@Component({
    selector: 'ms-regular-option-group',
    templateUrl: './regular-option-group.component.html',
    styleUrl: './regular-option-group.component.scss',
    providers: [ParticipantImageService, OptionGroupPlayerStatisticsService, BaseOptionGroupStatisticsService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MarketHelpComponent,
        OptionGroupRendererDirective,
        RxFor,
        OptionComponent,
        OptionPanelBottomActionComponent,
        IconCustomComponent,
        ParticipantImageComponent,
        PlayerStatsComponent,
        NativePriceFormatterPipe,
        NgTemplateOutlet,
        PlayerStatisticsComponent,
        IconCustomComponent,
    ],
})
export class RegularOptionGroupComponent implements OnInit, OnChanges {
    sitecore = inject(Sitecore);
    private trackingService = inject(TrackingService);
    private epcotConfig = inject(EpcotConfig);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private participantImageService = inject(ParticipantImageService);

    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';

    optionGroup = signal<EventOptionGroup>(null!);
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input({ alias: 'optionGroup', required: true })
    set _optionGroup(optionGroup: EventOptionGroup) {
        this.optionGroup.set(optionGroup);
    }

    @Input() thresholds?: OptionPanelThreshold[];
    @Input() showMarketHelp: boolean;
    @Input() crmPromotedOptionIdsMap: { [optionId: number]: number } = {};
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Input() calendarTrack?: string;
    @Input() groupType: OptionGroupType;
    @Input() showPlayerStats = false;

    @Output() expandClicked = new EventEmitter<boolean>();
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    readonly optionGroupPlayerStatisticsService = inject(OptionGroupPlayerStatisticsService);
    threshold = signal(0);
    more = signal(false);
    isExpanded = signal(false);
    isEpcotEnabled = false;
    isUSGroupedListEnabled = false;
    trackingSource: TrackingSource;
    participantType = ParticipantType;

    useMsOptionRenderer = false;
    optionImages: NumberDictionary<EventParticipantImage> = {};
    isShowOptionImage = false;
    visibleOptions = computed(() => {
        if (this.isInSheetView || this.isExpanded() || !this.threshold()) {
            return this.optionGroup().options;
        }

        return this.optionGroup().options.slice(0, this.threshold());
    });

    columns = signal(1);

    visibleRows = computed(() => Math.floor(this.visibleOptions().length / this.columns()));

    visibleOptions$ = toObservable(this.visibleOptions);

    columnsDetermined = false;

    showMoreData = computed(() => {
        const optionCount = this.optionGroup().options.length;

        return {
            totalCount: optionCount,
            initialVisibleCount: this.threshold() || optionCount,
            pageSize: optionCount,
        };
    });

    isShowHeadshotImage = false;
    playerStatisticsService = inject(PlayerStatisticsService);
    readonly playerStatisticsMessages = this.playerStatisticsService.playerStatisticsMessages;
    readonly seasonStatisticsMessage = this.playerStatisticsService.seasonStatisticsMessage;
    readonly SeasonStatisticsType = SeasonStatisticsType;

    constructor() {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnInit() {
        this.isUSGroupedListEnabled = this.groupType === OptionGroupType.USGroupedList;
        this.useMsOptionRenderer = !this.isEpcotEnabled && !this.isUSGroupedListEnabled;
        if (this.isUSGroupedListEnabled) {
            this.columns.set(2);
        }
        if (!this.useMsOptionRenderer) {
            this.threshold.set(this.calcThreshold(this.isUSGroupedListEnabled ? 2 : 1));
        }
        this.trackingSource = { source: this.tracking, calendarTitle: this.calendarTrack, moduleType: this.trackingModuleType };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sourceOfPick || changes.event) {
            this.isShowOptionImage = this.participantImageService.showOptionsImage(this.event, this.optionGroup());
            this.isShowHeadshotImage =
                this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.Regular) &&
                isPlayerMatched(this.event.players, this.optionGroup().options);
            if (this.isShowOptionImage) {
                this.getPlayerImages(this.optionGroup());
            }
            if (this.isShowHeadshotImage) {
                this.optionGroup().options.forEach((option) => {
                    if (option.image) {
                        this.optionImages[option.id] = option.image;
                    }
                });
            }
        }
        if (
            changes._optionGroup?.previousValue !== changes._optionGroup?.currentValue ||
            changes.event?.previousValue !== changes.event?.currentValue
        ) {
            this.optionGroupPlayerStatisticsService.initializePlayerStatistics(this.event, OptionGroupType.Regular);
            this.optionGroupPlayerStatisticsService.fetchPlayerStatistics(this.event, this.optionGroup());
        }
    }

    @HostBinding('class.center-align-option') get isCorrectScore() {
        const dynamicCorrectScore_MarketType = this.optionGroup().originalParameters?.find(
            (param) => param.key === Tv2MarketParameters.MarketType && param.value === 'DynamicCorrectScore',
        );
        const points_Happening = this.optionGroup().originalParameters?.find(
            (param) => param.key === Tv2MarketParameters.Happening && param.value === 'Points',
        );

        return dynamicCorrectScore_MarketType && points_Happening;
    }

    @HostBinding('class') get borderStyle(): string {
        if (this.isUSGroupedListEnabled && this.optionGroup().options.length % 2 === 0) {
            return 'even-options';
        }

        return '';
    }

    @HostBinding('class') get participantJersey(): string {
        if (this.isShowOptionImage) {
            return 'jersey-panel';
        }

        return '';
    }

    borderClass(index: number): boolean {
        return (
            !this.isExpanded() &&
            this.threshold() > 0 &&
            this.optionGroup().options.length > this.threshold() &&
            (index === this.threshold() - 1 || index === this.threshold() - 2)
        );
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.isExpanded.set(state !== 'collapsed');
        this.publishExpandedEvent();
    }

    expand(): void {
        this.more.set(!this.more());
        this.expandClicked.emit(this.more());

        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.more() ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    private publishExpandedEvent(): void {
        this.expandClicked.emit(this.isExpanded());
        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.isExpanded() ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    trackOption(index: number, option: EventOption): number {
        return option.id;
    }

    showOption(index: number): boolean {
        return this.more() || !this.threshold() || index < this.threshold();
    }

    rendererChange(columns: number): void {
        this.threshold.set(this.calcThreshold(columns));
        this.columns.set(columns);
        if (this.useMsOptionRenderer && !this.columnsDetermined) {
            this.columnsDetermined = true;
        }
    }

    togglePlayerStatistics(option: EventOption): void {
        const params = this.extractParams(option);
        this.optionGroupPlayerStatisticsService.toggleOpen(this.event, params, this.optionGroup());
    }

    isPlayerStatisticsOpen(option: EventOption): boolean {
        const params = this.extractParams(option);
        return this.optionGroupPlayerStatisticsService.isOpen(params);
    }

    private extractParams(option: EventOption): StatisticsParams {
        const optionGroupId = option.optionGroupId?.toString();
        const optionId = option.id.toString();
        return {
            optionGroupId,
            playerName: option.name,
            key: `${option.optionGroupId}_${option.playerId}`,
            optionId,
        };
    }

    protected calcThreshold(columns: number) {
        if (this.optionGroup().detailedGrouping?.displayType === ExtendedDisplayType.AutomatedPriceBoost) {
            return this.optionGroup().options.length;
        }
        let threshold = this.marketGroupingConfig.showMoreRegularThreshold;
        if (this.optionsLimitOverride) {
            threshold = this.optionsLimitOverride;
        } else if (this.thresholds) {
            const active = this.thresholds
                .filter((current) => current.columns <= columns)
                .sort((first, second) => first.columns - second.columns)
                .pop();

            if (active) {
                threshold = active.threshold;
            }

            threshold = ceil(threshold / columns) * columns;
        }

        return threshold;
    }

    private getPlayerImages(optionGroup: EventOptionGroup): void {
        const options = this.participantImageService.getPlayerJersey(this.event, optionGroup);

        this.optionImages = options?.reduce(
            (acc, option) => {
                acc[option.id] = this.participantImageService.getParticipantImage(option, this.event.sport?.id, this.event.offerSource, true);

                return acc;
            },
            {} as Record<number, EventParticipantImage>,
        );
    }
}
